'use client';

// import type { Analytics } from '@firebase/analytics';
// import { getAnalytics, setUserId } from '@firebase/analytics';
import { initializeApp } from 'firebase/app';
// import { ReCaptchaEnterpriseProvider, initializeAppCheck } from 'firebase/app-check';
import type { User } from 'firebase/auth';
import {
  GoogleAuthProvider,
  OAuthProvider,
  applyActionCode,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  getAuth,
  getRedirectResult,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  verifyPasswordResetCode
} from 'firebase/auth';
import { fetchAndActivate, getRemoteConfig, getValue } from 'firebase/remote-config';

import { loginThirdPart } from '@/service/login';

import { firebaseConfig } from './host';
import reportClick, { EReportClickKey } from './report/reportClick';

// 全局变量，用于存储 Firebase 实例
let _app: ReturnType<typeof initializeApp> | null = null;
let _auth: ReturnType<typeof getAuth> | null = null;
let _remoteConfig: any = null;
// let _analytics: Analytics | null = null;

// 初始化 Firebase (同步版本)
const initializeFirebase = () => {
  if (_app && _auth) return { app: _app, auth: _auth };

  const config = firebaseConfig();

  if (!_app) {
    _app = initializeApp(config);
  }

  if (!_auth) {
    _auth = getAuth(_app);
  }

  // 初始化 remoteConfig
  if (typeof window !== 'undefined') {
    _remoteConfig = getRemoteConfig(_app);
    _remoteConfig.defaultConfig = {
      awesome_feature6: 'https://mindverseglobal.mindverse.com/assets/operation/default_logo.svg'
    };
  }

  return { app: _app, auth: _auth };
};

export const getFirebaseRemoteConfig = (item: string) => {
  if (typeof window !== 'undefined') {
    // 确保 Firebase 已初始化
    initializeFirebase();

    if (_remoteConfig) {
      fetchAndActivate(_remoteConfig);

      return getValue(_remoteConfig, item).asString();
    }
  }

  return '';
};

// export const firebaseAnalytics = () => {
//   if (!_analytics) {
//     const { app } = initializeFirebase();

//     _analytics = getAnalytics(app);
//   }

//   return _analytics;
// };

// export const setupUserId = (userId: string | null): void => {
//   setUserId(firebaseAnalytics(), userId);
// };
/**
 * Google 登陆
 * @returns 登陆的用户 promise
 */
export const loginWithGoogle = () => {
  const provider = new GoogleAuthProvider();
  const { auth } = initializeFirebase();

  const visitHomepageUserId = sessionStorage.getItem('visitHomepageUserId');

  reportClick(EReportClickKey.login, { key1: 'google', key5: visitHomepageUserId ?? '' });
  localStorage.setItem('loginType', 'google');

  return new Promise<User>((resolve, reject) => {
    signInWithPopup(auth, provider)
      .then((googleLogin) => {
        const user = googleLogin.user;

        resolve(user);
      })
      .catch((error) => {
        reportClick(EReportClickKey.login_error, {
          key1: 'google',
          key2: error.code ?? '',
          key3: error.message ?? ''
        });
        reject(error);
      });
  });
};

/**
 * Apple 登陆
 * @returns 登陆的用户 promise
 */
export const loginWithApple = () => {
  const provider = new OAuthProvider('apple.com');
  const { auth } = initializeFirebase();

  provider.addScope('email');
  provider.addScope('name');

  const visitHomepageUserId = sessionStorage.getItem('visitHomepageUserId');

  reportClick(EReportClickKey.login, { key1: 'apple', key5: visitHomepageUserId ?? '' });
  localStorage.setItem('loginType', 'apple');

  return new Promise<User>((resolve, reject) => {
    signInWithPopup(auth, provider)
      .then((result) => {
        const user = result.user;

        resolve(user);
      })
      .catch((error) => {
        reportClick(EReportClickKey.login_error, {
          key1: 'apple',
          key2: error.code ?? '',
          key3: error.message ?? ''
        });
        reject(error);
      });
  });
};

/**
 * Google 登陆 (Redirect 模式，兼容性更好)
 */
export const loginWithGoogleRedirect = () => {
  const provider = new GoogleAuthProvider();
  const { auth } = initializeFirebase();

  // 保存当前页面 URL，登录成功后可以跳回
  sessionStorage.setItem('loginRedirectUrl', window.location.href);

  return signInWithRedirect(auth, provider);
};

/**
 * Apple 登陆 (Redirect 模式，兼容性更好)
 */
export const loginWithAppleRedirect = () => {
  const provider = new OAuthProvider('apple.com');
  const { auth } = initializeFirebase();

  provider.addScope('email');
  provider.addScope('name');

  // 保存当前页面 URL，登录成功后可以跳回
  sessionStorage.setItem('loginRedirectUrl', window.location.href);

  return signInWithRedirect(auth, provider);
};

/**
 * 处理 redirect 登录结果，应在应用初始化时调用
 * @returns 如果有登录结果返回 User，否则返回 null
 */
export const handleRedirectResult = async (): Promise<User | null> => {
  const { auth } = initializeFirebase();

  try {
    const result = await getRedirectResult(auth);

    if (result?.user) {
      const visitHomepageUserId = sessionStorage.getItem('visitHomepageUserId');
      const provider = result.providerId === 'apple.com' ? 'apple' : 'google';

      reportClick(EReportClickKey.login, { key1: provider, key5: visitHomepageUserId ?? '' });
      localStorage.setItem('loginType', provider);

      return result.user;
    }

    return null;
  } catch (error) {
    console.error('handleRedirectResult error:', error);

    return null;
  }
};

// 在应用初始化时调用
// useEffect(() => {
//   handleRedirectResult().then((user) => {
//     if (user) {
//       // 有用户信息，说明是从 OAuth redirect 回来的
//       loginMindOSWithFirebaseUser(user).then((res) => {
//         // 处理登录成功逻辑
//         const redirectUrl = sessionStorage.getItem('loginRedirectUrl');
//         sessionStorage.removeItem('loginRedirectUrl');

//         if (redirectUrl) {
//           window.location.href = redirectUrl;
//         }
//       });
//     }
//   });
// }, []);

export const loginMindOSWithFirebaseUser = (user: User) => {
  return user.getIdToken().then((res) => {
    const origin = localStorage.getItem('isFromWebsite') === 'true' ? 'quiz' : undefined;
    // const via = localStorage.getItem('rewardful_via') ?? undefined;

    localStorage.removeItem('isFromWebsite');
    // localStorage.removeItem('rewardful_via');

    return loginThirdPart({
      idToken: res,
      refPlatform: 'firebase',
      origin,
      // via,
      sourceId: 'this is from client'
      // inviteCode: localStorage.getItem('inviteCode') || undefined
    });
  });
};

/**
 * 忘记密码，重新发送邮件
 * @param mail 邮箱地址
 * @returns
 */
export const forgetPassword = (mail: string) => {
  const { auth } = initializeFirebase();

  return new Promise<void>((resolve, reject) => {
    sendPasswordResetEmail(auth, mail)
      .then(() => {
        reportClick(EReportClickKey.forget_password);

        resolve();
      })
      .catch((error) => {
        reject(error);
      });
  });
};

/**
 * 通过邮箱登陆
 * @param mail 邮箱登陆
 * @param password 密码
 * @returns
 */
export const loginWithEmail = (mail: string, password: string) => {
  const { auth } = initializeFirebase();

  const visitHomepageUserId = sessionStorage.getItem('visitHomepageUserId');

  reportClick(EReportClickKey.login, { key1: 'email', key5: visitHomepageUserId ?? '' });
  localStorage.setItem('loginType', 'email');

  return new Promise<User>((resolve, reject) => {
    signInWithEmailAndPassword(auth, mail, password)
      .then((res) => {
        const user = res.user;

        resolve(user);
      })
      .catch((error) => {
        reportClick(EReportClickKey.login_error, {
          key1: 'email',
          key2: error.code ?? '',
          key3: error.message ?? ''
        });
        console.error(error);
        reject(error);
      });
  });
};

/**
 * 登出
 */
export const firebaseSignOut = () => {
  const { auth } = initializeFirebase();

  reportClick(EReportClickKey.logout);

  if (auth.currentUser) {
    auth
      .signOut()
      .then(() => {
        console.log('logout firebase success');
      })
      .catch((error: any) => {
        console.error('filebase sign out failed', error);
      });
  }
};

/**
 * 邮箱注册
 * @param email 注册的邮箱
 * @param password 密码
 * @returns
 */
export const signUpWithEmail = (email: string, password: string) => {
  const { auth } = initializeFirebase();

  return createUserWithEmailAndPassword(auth, email, password)
    .then((userCredential) => {
      const user = userCredential.user;

      if (!user.emailVerified) {
        reportClick(EReportClickKey.sign_up, { key1: 'email' });

        return sendEmail(user);
      }

      throw new Error('Your email is already registered. Please try logging in.');
    })
    .then((user) => {
      if (user) {
        return user;
      }

      throw new Error('no user');
    })
    .catch((error) => {
      if (error.code === 'auth/email-already-in-use') {
        return resendVerifyEmail(email, password);
      }

      throw error;
    });
};

/**
 * 重发验证邮件
 */
export const resendVerifyEmail = (email: string, password: string) => {
  const { auth } = initializeFirebase();

  return new Promise<User>((resolve, reject) => {
    signInWithEmailAndPassword(auth, email, password)
      .then((res) => {
        if (!res.user.emailVerified) {
          return sendEmail(res.user);
        }

        throw new Error('Your email is already registered. Please try logging in.');
      })
      .then((user) => {
        resolve(user);
      })
      .catch((error) => {
        console.error(error);
        reject(error);
      });
  });
};

/**
 * 发送邮件
 */
export const sendEmail = (user: User) => {
  return new Promise<User>((resolve, reject) => {
    sendEmailVerification(user)
      .then(() => {
        resolve(user);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const handleVerifyEmail = (actionCode: string) => {
  const { auth } = initializeFirebase();

  return new Promise<void>((resovle, reject) => {
    // Localize the UI to the selected language as determined by the lang
    // parameter.
    // Try to apply the email verification code.
    applyActionCode(auth, actionCode)
      .then(() => {
        // Email address has been verified.
        reportClick(EReportClickKey.verify_email);
        resovle();
        // TODO: Display a confirmation message to the user.
        // You could also provide the user with a link back to the app.

        // TODO: If a continue URL is available, display a button which on
        // click redirects the user back to the app via continueUrl with
        // additional state determined from that URL's parameters.
      })
      .catch((error) => {
        // Code is invalid or expired. Ask the user to verify their email address
        // again.
        reject(error);
      });
  });
};

export const resetPassword = (oobCode: string, newPassword: string) => {
  const { auth } = initializeFirebase();

  return new Promise<void>((resolve, reject) => {
    // Verify the password reset code is valid.
    verifyPasswordResetCode(auth, oobCode)
      .then(() => {
        // Save the new password.
        confirmPasswordReset(auth, oobCode, newPassword)
          .then(() => {
            // Login with the new password if it was successful
            reportClick(EReportClickKey.reset_password);
            resolve();
          })
          .catch((error) => {
            console.error(error);
            reject(error);
          });
      })
      .catch((error) => {
        console.error(error);
        reject(error);
      });
  });
};

export const formatFirebaseErrorMsg = (error: any): string => {
  const code = error.code;

  switch (code) {
    case 'auth/too-many-requests':
      return 'Too many operations at the same time. Please try again later.';
    case 'auth/wrong-password':
      return 'Wrong password. You can reset your password when logging in.';
    case 'auth/internal-error':
      return 'Internet error.';
    case 'auth/popup-blocked':
      return `Access failed. Please verify if your browser's pop-up settings are enabled.`;
    default:
      return error.message;
  }
};
